import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getFieldValidator } from './personal.validators';
import { setRegion } from '../region/regionSlice';
import { config } from '../../config';
import type { RootState } from '../../configuration/setup/store';
import { useAppSelector } from '../../configuration/setup/hooks.ts';

export type PersonalInfoField = keyof PersonalInfoState;

export type PersonalToggles = keyof PersonalCheckBoxes;

interface PersonalCheckBoxes {
    newsletterApproved: boolean;
    termsAccepted: boolean;
    combinedServiceCareRequestedAndEulaAccepted: boolean;
}

interface PersonalData {
    firstName: string;
    firstNameValidationMessage?: string | null;
    firstNameTouched?: boolean;
    lastName: string;
    lastNameValidationMessage?: string | null;
    lastNameTouched?: boolean;
    email: string;
    emailValidationMessage?: string | null;
    emailTouched?: boolean;
    phoneNumber?: string;
    phoneNumberValidationMessage?: string | null;
    phoneNumberTouched?: boolean;
}

export type PersonalInfoState = PersonalData & PersonalCheckBoxes;

const initialState: PersonalInfoState & PersonalCheckBoxes = {
    firstName: '',
    firstNameValidationMessage: '',
    firstNameTouched: false,
    lastName: '',
    lastNameValidationMessage: '',
    lastNameTouched: false,
    email: '',
    emailValidationMessage: '',
    emailTouched: false,
    phoneNumber: '',
    phoneNumberValidationMessage: null,
    phoneNumberTouched: false,
    newsletterApproved: false,
    termsAccepted: false,
    combinedServiceCareRequestedAndEulaAccepted: false,
};

const personalInfoSlice = createSlice({
    name: 'personal',
    initialState,
    reducers: {
        setPersonalInfoField: (state, action: PayloadAction<{ fieldName: PersonalInfoField; value: string }>) => {
            const { fieldName, value } = action.payload;
            const nextState = {
                ...state,
                [fieldName]: value,
            };

            const validProperty = `${fieldName}ValidationMessage`;
            const validator = getFieldValidator(fieldName) || (() => true);
            return {
                ...nextState,
                [validProperty]: validator(value),
            };
        },
        touchPersonalInfoField: (state, action: PayloadAction<PersonalInfoField>) => {
            const fieldName = action.payload;

            // we dynamically add a "[fieldName]Touched" property with value true
            // which is otherwise "undefined" and because of that false
            const propertyName = `${fieldName}Touched`;

            return {
                ...state,
                [propertyName]: true,
            };
        },
        togglePersonalCheckbox: (state, action: PayloadAction<PersonalToggles>) => {
            const fieldName = action.payload;

            return {
                ...state,
                [fieldName]: !state[fieldName],
            };
        },
    },
    extraReducers: builder => {
        builder.addCase(setRegion, state => {
            if (config.isTestTenantMode) {
                state.firstName = 'TestUserFirstName';
                state.firstNameValidationMessage = null;
                state.lastName = 'TestUserLastName';
                state.lastNameValidationMessage = null;
            }
        });
    },
});

export const { setPersonalInfoField, touchPersonalInfoField, togglePersonalCheckbox } = personalInfoSlice.actions;

export const personalInfoReducer = personalInfoSlice.reducer;

export const getPersonalInfoEmail = (state: RootState) => state.personalInfo.email;

// Selectors
const getNewsletterApproved = (state: RootState) => state.personalInfo.newsletterApproved;
export const useNewsletterApproved = () => useAppSelector(getNewsletterApproved);
const getTermsAccepted = (state: RootState) => state.personalInfo.termsAccepted;
export const useTermsAccepted = () => useAppSelector(getTermsAccepted);
const getCombinedServiceCareRequestedAndEulaAccepted = (state: RootState) =>
    state.personalInfo.combinedServiceCareRequestedAndEulaAccepted;
export const useCombinedServiceCareRequestedAndEulaAccepted = () =>
    useAppSelector(getCombinedServiceCareRequestedAndEulaAccepted);
